import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress, Link, Stack, Typography } from '@mui/material';
import {
  ArrowRightIcon,
  Cancel,
  Clock,
  CountDown,
  Email,
  LinkIcon,
  OpayIcon,
  TelegramIcon,
  WhatsAppIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours, globalShadows } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyGetPaymentQuery } from '../../services/payment';
import { PaymentStatuses } from '@classity-edtech/platform-utils';
import { useEffect, useState } from 'react';
import { webPaths } from '../../routes/paths';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import Helmet from '../../components/Helmet';

export const PaymentLinkShare = () => {
  const dispatch: any = useAppDispatch();
  const isSharedLink = false;
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cashierUrl = searchParams.get('cashierUrl');
  const reference = searchParams.get('reference');
  const transactionId = searchParams.get('transactionId');
  const [loadingDelay, setLoadingDelay] = useState(true);

  const futureTime = dayjs().add(30, 'minute').valueOf();

  const isWithinFiveMinutes = dayjs().isAfter(dayjs(futureTime).subtract(5, 'minute'));

  const [payment, paymentResult] = useLazyGetPaymentQuery();

  const text = `Could you please assist me with the payment for my tuition at Classity. Go to ${decodeURI(cashierUrl)} to complete the payment. It is safe and secure.`;

  const encodedText = encodeURIComponent(text);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodedText}`,
    email: `mailto:?body=${encodedText}`,
    telegram: `https://t.me/share/url?text=${encodedText}`,
  };

  const handleCopyToClipBoard = () => {
    navigator.clipboard.writeText(decodeURI(cashierUrl));

    dispatch(
      updateFlashMessage({
        open: true,
        severity: 'success',
        message: 'Payment link copied!',
      }),
    );
  };

  useEffect(() => {
    if (!reference) return;

    payment({ id: reference });

    const intervalId = setInterval(() => {
      payment({ id: reference });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [reference]);

  useEffect(() => {
    if (paymentResult?.data?.order?.paymentStatus === PaymentStatuses.SUCCEEDED) {
      navigate(
        `/${webPaths?.orderConfirmation}?paymentId=${reference}&transactionId=${transactionId}&status=${'success'}`,
      );
    }
    if (paymentResult?.data?.order?.paymentStatus === PaymentStatuses.FAILED) {
      navigate(
        `/${webPaths?.orderConfirmation}?paymentId=${reference}&transactionId=${transactionId}&status=${'failed'}`,
      );
    }
  }, [paymentResult?.data?.order?.paymentStatus]);

  useEffect(() => {
    if (paymentResult.isLoading) {
      setLoadingDelay(true);
    } else {
      let loadingTimer = setTimeout(() => {
        setLoadingDelay(false);
      }, 3000);

      return () => clearTimeout(loadingTimer);
    }
  }, [paymentResult.isLoading]);

  return (
    <>
      <Helmet />
      <Box
        sx={{
          width: '100dvw',
          height: { xs: 'fit-content', md: '100dvh' },
          maxHeight: '100dvh',
          overflow: 'auto',
          display: { xs: 'block', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Cancel
          sx={{
            width: '16px',
            height: '16px',
            mt: 3,
            mr: 3,
            ml: 'auto',
            display: { xs: 'block', md: 'none' },
            cursor: 'pointer',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 4,
            px: 3,
            py: 2,
            maxWidth: '800px',
            mx: 'auto',
            borderRadius: '16px',
            boxShadow: { xs: 'none', md: globalShadows.small },
            width: { xs: '100%', md: '800px' },
            height: { xs: 'max-content', md: '520px' },
          }}
        >
          {loadingDelay && (
            <Container
              sx={{
                display: 'flex',
                borderRadius: '16px',
                flexDirection: 'column',
                mx: { xs: 0, md: 'auto' },
                px: 0,
                background: globalColours['$global-colour-background-elevated'],
                position: 'relative',
                height: '100%',
              }}
            >
              <Cancel
                sx={{
                  width: '16px',
                  height: '16px',
                  my: 2,
                  ml: 'auto',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                }}
              />

              <Stack
                sx={{
                  pt: 3,
                  pb: 2,
                  height: '100%',
                }}
              >
                <Typography variant='subtitle4' sx={{ display: 'block' }}>
                  Checking Payment Status...
                </Typography>

                <Stack
                  sx={{
                    width: '100%',
                    height: { xs: '70dvh', md: '100%' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <CircularProgress sx={{ width: '48px', height: '48px' }} />
                  <Typography variant='body3'>Please wait while we confirm payment...</Typography>
                </Stack>
              </Stack>
            </Container>
          )}

          {!loadingDelay && (
            <Container
              sx={{
                display: 'flex',
                borderRadius: '16px',
                flexDirection: 'column',
                mx: { xs: 0, md: 'auto' },
                px: 0,
                background: globalColours['$global-colour-background-elevated'],
                position: 'relative',
                overflowY: 'auto',
                height: '100%',
              }}
            >
              <Cancel
                sx={{
                  width: '16px',
                  height: '16px',
                  my: 2,
                  ml: 'auto',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                }}
              />
              <Stack
                sx={{
                  pt: 3,
                  pb: 2,
                }}
              >
                <Typography
                  variant='subtitle4'
                  sx={{
                    display: 'block',
                    position: 'sticky',
                    top: 0,
                    background: globalColours['$global-colour-background-elevated'],
                    zIndex: 5,
                  }}
                >
                  Share Payment Link
                </Typography>

                <Typography variant='body3'>
                  Ask one of your parents, relative, or friend to make payment on your behalf by
                  sharing the payment link below. The process is safe and secure.
                </Typography>
              </Stack>

              <Stack sx={{ gap: 2 }}>
                {!(isSharedLink && isSmallScreen) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'space-between', md: 'center' },
                      gap: 1,
                      mb: 1,
                      width: '100%',
                    }}
                  >
                    <Link
                      sx={{
                        color: globalColours['$global-colour-content-primary'],
                        textDecoration: 'none',
                      }}
                      href={shareLinks.whatsapp}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <Stack sx={{ alignItems: 'center', p: 1 }}>
                        <WhatsAppIcon sx={{ width: '16px', height: '16px', color: '#0B891F' }} />
                        <Typography variant='body3'>WhatsApp</Typography>
                      </Stack>
                    </Link>
                    <Link
                      sx={{
                        color: globalColours['$global-colour-content-primary'],
                        textDecoration: 'none',
                      }}
                      href={shareLinks.email}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <Stack sx={{ alignItems: 'center', p: 1 }}>
                        <Email sx={{ width: '16px', height: '16px' }} />
                        <Typography variant='body3'>Email</Typography>
                      </Stack>
                    </Link>
                    <Link
                      sx={{
                        color: globalColours['$global-colour-content-primary'],
                        textDecoration: 'none',
                      }}
                      href={shareLinks.telegram}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <Stack sx={{ alignItems: 'center', p: 1 }}>
                        <TelegramIcon sx={{ width: '16px', height: '16px' }} />
                        <Typography variant='body3'>Telegram</Typography>
                      </Stack>
                    </Link>
                    <Stack
                      sx={{ alignItems: 'center', p: 1, cursor: 'pointer' }}
                      onClick={handleCopyToClipBoard}
                    >
                      <LinkIcon sx={{ width: '16px', height: '16px' }} />
                      <Typography variant='body3'>Copy</Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      borderRadius: '100px',
                      backgroundColor: globalColours['$global-colour-interactive-accent-default'],
                      color: globalColours['$global-colour-base-contrast'],
                      width: '116px',
                      height: '48px',
                      padding: '5px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      mx: 'auto',
                    }}
                  >
                    <LinkIcon sx={{ width: '14px', height: '14px' }} />
                    <Typography variant='body2SemiBold'>Share</Typography>
                  </Box>
                )}

                <Stack
                  sx={{
                    maxWidth: { xs: '250px', md: '100%' },
                    mx: 'auto',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant='body3'>
                    This payment link is valid for this booking only and will expire in{' '}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Clock
                      sx={{
                        width: '16px',
                        height: '16px',
                        color: isWithinFiveMinutes
                          ? globalColours['$global-colour-sentiment-error-default']
                          : '',
                      }}
                    />
                    <Typography
                      variant='body3SemiBold'
                      sx={{
                        color: isWithinFiveMinutes
                          ? globalColours['$global-colour-sentiment-error-default']
                          : '',
                      }}
                    >
                      <CountDown start customized endTime={futureTime} endAction={() => {}} />
                    </Typography>
                  </Box>
                </Stack>

                {!(isSharedLink && isSmallScreen) && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      backgroundColor: globalColours['$global-colour-palette-gray-50'],
                      border: `solid 1px ${globalColours['$global-colour-interactive-secondary-default']}`,
                      borderRadius: '10px',
                      maxWidth: '330px',
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={handleCopyToClipBoard}
                    >
                      {decodeURI(cashierUrl)}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  py: 3,
                  gap: 4,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'sticky',
                  bottom: 0,
                  mt: 'auto',
                  background: globalColours['$global-colour-background-elevated'],
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pr: '6px',
                      mr: 0.5,
                      py: 0.5,
                      borderRight: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                    }}
                  >
                    <OpayIcon sx={{ height: '12px', width: '100%' }} />
                  </Box>
                  <Typography variant='body3'>
                    A licensed and regulated payment service provider.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 1,
                    color: globalColours['$global-colour-interactive-control-default'],
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant='body3'>Check Payment Status</Typography>
                  <ArrowRightIcon sx={{ width: '24px', height: '24px' }} />
                </Box>
              </Box>
            </Container>
          )}
        </Box>

        {!loadingDelay && (
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: globalColours['$global-colour-interactive-control-default'],
              py: 1,
              px: 3,
              position: 'fixed',
              bottom: 0,
              width: '100%',
              minHeight: '54px',
              boxShadow: globalShadows.small,
            }}
          >
            <Typography variant='body3'>Check Payment Status</Typography>
            <ArrowRightIcon sx={{ width: '24px', height: '24px' }} />
          </Box>
        )}
      </Box>
    </>
  );
};

PaymentLinkShare.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

PaymentLinkShare.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(PaymentLinkShare)(
  ({ theme }) => `
`,
);
